import * as React from 'react';
import {
  Button,
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  ProgressBar,
  Text,
  VStack,
} from '@packages/systems/spring';
import * as persistentUIState from '@packages/systems/designer/persistent-state';
import {ToolsLayerView} from '@packages/systems/ui/shared-ui/ToolsLayerView';
import type {MCCGuardrailsPolicyType} from '@packages/systems/multi-context-canvas/store';
import {emotionStyled} from '@packages/utilities/styled';

const CapacityIndicatorToggleButton = emotionStyled(IconButton)({
  '&[aria-expanded="true"]': {
    background: 'transparent',
  },
});

type RevealFrameConfirmationPopoverProps = {
  children: React.ReactElement;
  isRequired: boolean;
  onReveal: () => void;
  placement?: React.ComponentProps<typeof Popover>['placement'];
};

export const RevealFrameConfirmationPopover = ({
  children,
  isRequired,
  onReveal,
  placement = 'bottom-end',
}: RevealFrameConfirmationPopoverProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [shouldDismissConfirmation, setShouldDismissConfirmation] =
    React.useState(false);

  const handleShowAnyway = React.useCallback(() => {
    if (shouldDismissConfirmation) {
      persistentUIState.merge({hasDismissedMCCRevealConfirmation: true});
    }
    setShouldDismissConfirmation(false);
    setIsOpen(false);
    onReveal();
  }, [onReveal, shouldDismissConfirmation]);

  const handleOpenChange = React.useCallback((nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    if (!nextIsOpen) {
      setShouldDismissConfirmation(false);
    }
  }, []);

  if (!isRequired) return children;

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placement={placement}
      shouldHideOnClickOutside
      width="240px"
    >
      <PopoverTrigger
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          if (
            persistentUIState.get().hasDismissedMCCRevealConfirmation === true
          ) {
            onReveal();
            return;
          }
          setIsOpen(true);
        }}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        aria-label="Show breakpoint contents confirmation"
        hasSoftCorners={false}
        isSeamless
        onClick={(event) => event.stopPropagation()}
      >
        <PopoverHeader hasCloseButton title="Show breakpoint contents?" />
        <PopoverBody padding="$2">
          <VStack>
            <Text colorVariant="secondary">
              Showing these contents may slow the canvas for this page.
            </Text>
            <Checkbox
              isChecked={shouldDismissConfirmation}
              label="Don't show this again"
              onChange={(event) =>
                setShouldDismissConfirmation(event.target.checked)
              }
            />
          </VStack>
        </PopoverBody>
        <PopoverFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Keep hidden
          </Button>
          <Button colorVariant="primary" onClick={handleShowAnyway}>
            Show anyway
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

type MCCCapacityIndicatorProps = {
  policyType: MCCGuardrailsPolicyType | null;
  hasHiddenFrames: boolean;
};

export const MCCCapacityIndicator = ({
  policyType,
  hasHiddenFrames,
}: MCCCapacityIndicatorProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  if (policyType !== 'medium' && policyType !== 'high') {
    return null;
  }

  const isPolicyTypeHigh = policyType === 'high';

  const statusText = hasHiddenFrames
    ? 'Some breakpoints are hidden'
    : 'All breakpoints are visible';
  const hiddenFramesDescription = isPolicyTypeHigh
    ? 'This page has a lot of content. Hiding inactive breakpoints helps performance.'
    : 'This page has a lot of content. Hiding some breakpoints helps performance.';

  return (
    <ToolsLayerView
      position="absolute"
      right="$3"
      bottom="$3"
      data-automation-id="mcc-capacity-indicator"
    >
      <VStack
        width="200px"
        padding="$3"
        backgroundColor="$backgroundPrimary"
        borderRadius="$4"
      >
        <Collapsible isOpen={!isCollapsed}>
          <HStack align="center" justify="space-between">
            <Text
              font="body"
              weight="semibold"
              whiteSpace="nowrap"
              role="status"
            >
              {statusText}
            </Text>
            <CollapsibleTrigger
              asChild
              onClick={() => setIsCollapsed((current) => !current)}
            >
              <CapacityIndicatorToggleButton
                Icon={isCollapsed ? ChevronSmallUpIcon : ChevronSmallDownIcon}
                density="compact"
                label={
                  isCollapsed
                    ? 'Expand canvas performance details'
                    : 'Collapse canvas performance details'
                }
                variant="ghost"
              />
            </CollapsibleTrigger>
          </HStack>
          <CollapsibleContent asChild>
            <VStack>
              <ProgressBar
                aria-label="Canvas performance"
                aria-valuetext={statusText}
                colorVariant={isPolicyTypeHigh ? 'danger' : 'warning'}
                size="small"
                value={isPolicyTypeHigh ? 1 : 0.8}
              />
              <Text colorVariant="secondary">
                {hasHiddenFrames
                  ? hiddenFramesDescription
                  : 'All breakpoints are visible. The canvas may run slower on this page.'}
              </Text>
            </VStack>
          </CollapsibleContent>
        </Collapsible>
      </VStack>
    </ToolsLayerView>
  );
};
